import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Grid, Card, LinearProgress, CircularProgress, Alert, Paper } from "@mui/material";
import { getData } from '../../services/axiosInstance';
import { API_ENDPOINTS } from '../../services/endpoints';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

function LeaveBalanceSummary() {
    const [leaveData, setLeaveData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;

    const fetchLeaveBalance = useCallback(async () => {
        if (!userId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const url = API_ENDPOINTS.getAllLeaveTypesByEmployeeId(userId);
            const response = await getData(url);

            if (response?.status === "SUCCESS" && response?.message?.leaveTypesWithBalance) {
                setLeaveData(response.message.leaveTypesWithBalance);
            } else {
                setLeaveData([]);
            }
        } catch (err) {
            setError("Failed to load leave summary.");
            setLeaveData([]);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        fetchLeaveBalance();
    }, [fetchLeaveBalance]);

    const regularLeaves = leaveData.filter(leave => leave.allocated_days !== null);
    const otherLeaves = leaveData.filter(leave => leave.allocated_days === null);

    const LeaveCard = ({ leave, isRegular }) => {
        const used = leave?.used_days ?? 0;
        const total = leave?.allocated_days ?? 0;
        const remaining = leave?.remaining ?? 0;
        const progress = total > 0 ? (used / total) * 100 : 0;

        return (
            <Card sx={{ p: 2.5, borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', height: '100%' }}>
                <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 700, mb: 2 }}>
                    {leave?.leave_type || "N/A"}
                </Typography>

                <Box sx={{ mb: isRegular ? 3 : 1 }}>
                    {isRegular && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                            Total: <strong>{total} days</strong>
                        </Typography>
                    )}
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        Used: <strong style={{ color: '#1976d2' }}>{used} days</strong>
                    </Typography>
                    {isRegular && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Remaining: <strong style={{ color: '#2e7d32' }}>{remaining} days</strong>
                        </Typography>
                    )}
                </Box>

                {isRegular && (
                    <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                {Math.round(progress)}% used
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                {remaining} days left
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                                height: 8,
                                borderRadius: 5,
                                backgroundColor: '#eef2ff',
                                '& .MuiLinearProgress-bar': { backgroundColor: progress > 80 ? '#f44336' : '#4caf50' }
                            }}
                        />
                    </Box>
                )}
            </Card>
        );
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

    if (!leaveData || leaveData.length === 0) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 4, backgroundColor: '#fcfcfc', border: '1px dashed #ccc' }}>
                <InfoOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="h6" color="text.secondary">No Leave Data Available</Typography>
                <Typography variant="body2" color="text.disabled">We couldn't find any leave balances for your account.</Typography>
            </Paper>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, p: 1 }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {regularLeaves.length > 0 && (
                <>
                    <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 700, mb: 2 }}>Regular Leaves</Typography>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {regularLeaves.map((leave, idx) => (
                            <Grid size={{ xs: 12, sm: 6 }} key={leave.leave_type_id || idx}>
                                <LeaveCard leave={leave} isRegular={true} />
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {otherLeaves.length > 0 && (
                <>
                    <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 700, mb: 2 }}>Other Leaves</Typography>
                    <Grid container spacing={3}>
                        {otherLeaves.map((leave, idx) => (
                            <Grid size={{ xs: 12, sm: 6 }} key={leave.leave_type_id || idx}>
                                <LeaveCard leave={leave} isRegular={false} />
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
        </Box>
    );
}

export default LeaveBalanceSummary;